<template>
    <div class="data-status-grid">
        <loading-spinner :show-loading="showLoading"></loading-spinner>
        <information-modal
            :title="selectedErrorCode"
            v-model="showErrorDescriptionModal">
            <template #body>
                <div v-html="selectedErrorBody"></div>
            </template>
        </information-modal>

        <errors-warnings-update
            v-model="showUpdateModal"
            :item="selectedItem"
            :url="`${apiUrl}/actions/dismiss-error-warning`"
            :dataset="dataset"
            @dismiss="handleUpdateModalDismiss"></errors-warnings-update>

        <div class="d-flex justify-content-between align-items-end">
            <div class="form-check d-inline-block">
                <input
                    id="cbAddCol"
                    type="checkbox"
                    class="form-check-input"
                    v-model="showAdditionalColumns" />
                <label
                    for="cbAddCol"
                    class="form-check-label"
                    >Display with the latest explanation/justification for Status change</label
                >
            </div>
            <div
                class="btn-group ms-auto"
                style=""
                role="group">
                <drop-down
                    label="Error/Warning Details"
                    :item-count="2"
                    :is-primary-button="false">
                    <template v-slot:item1="{}">
                        <a
                            class="dropdown-item"
                            :href="`${store.errorDescriptionsDownloadUrl}csv`"
                            >Download as .csv</a
                        >
                    </template>
                    <template v-slot:item2="{}">
                        <a
                            class="dropdown-item"
                            :href="`${store.errorDescriptionsDownloadUrl}xlsx`"
                            >Download as .xlsx</a
                        >
                    </template>
                </drop-down>
            </div>
        </div>
        <b-grid
            caption="List of errors and warnings"
            :tableOptions="options"
            :filterableFields="filterableFields"
            :filterOptions="filterOptions"
            @optionChange="getData"
            :items="dataItems"
            :columns="columns"
            :loading="false"
            :current-item-count="numberOfReturnedItems">
            <template v-slot:cell.status="{item, column}">
                <button
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    @click.prevent="handleStatusClick(item)"
                    v-if="isStatusLinkEnabled(item)">
                    {{ item.status }}
                </button>
                <span v-else>{{ item.status }}</span>
            </template>
            <template #cell.incidentNumber="{item}">
                <a
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    :href="getEditorPageName(item)"
                    v-if="enableLinkToEditor"
                    >{{ item["incidentNumber"] }}</a
                >
                <span v-else>{{ item["incidentNumber"] }}</span>
            </template>
            <template #cell.decedentSurname="{item, column}">
                    <a
                        class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                        :href="getEditorPageName(item)"
                        v-if="enableLinkToEditor"
                        >{{ item["decedentSurname"] }}</a
                    >
                    <span v-else>{{ item["decedentSurname"] }}</span>
                </template>
            <template #cell.errorCode="{item}">
                <button
                    type="button"
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    v-on:click.prevent="handleErrorCodeClick(item)">
                    {{ item["errorCode"] }}
                </button>
            </template>
            <template #cell.vendorName="{item}">
                <p>{{ item.vendorName == null || item.vendorName?.length == 0 ? "Vendor Not Specified": item.vendorName }}</p>
            </template>
            <template #pagingStart>
                <div
                    class="btn-group"
                    role="group">
                    <drop-down
                        label="Export Data"
                        :item-count="2"
                        :is-primary-button="true">
                        <template v-slot:item1="{item}">
                            <a class="dropdown-item"
                               href="javascript:void"
                               @click.prevent="exportData('csv')">Download as .csv</a>
                        </template>
                        <template v-slot:item2="{item}">
                            <a class="dropdown-item"
                                href="javascript:void"
                                @click.prevent="exportData('xlsx')">Download as .xlsx</a>
                        </template>
                    </drop-down>
                </div>
            </template>
        </b-grid>
        <system-alerts></system-alerts>
    </div>
</template>
<script lang="ts" setup>
import {ref, computed, onMounted, watch} from "vue";
import axios from "axios";
import BGrid from "./BGrid/BGrid.vue";
import {serializeFilters, serializeSortBy} from "./BTable/HelperFunctions";
import LoadingSpinner from "./Misc/LoadingSpinner.vue";
import {BFilter} from "./BFilterEditor/BFilterEditor.types";

import {InformationModal, SystemAlerts} from "./Misc";
import {UploadType} from "@/types";
import {getHtmlContent} from "@/utils";
import {useCommunityPolicingStore} from "@/stores/communityPolicingStore";
import {useContactsStore} from "@/stores/contactsStore";
import {useNibrsStore} from "@/stores/nibrsStore";
import {useUofStore} from "@/stores/uofStore";
import {useSummaryStore} from "@/stores/summaryStore";
import {usePoliceEmployeeStore} from "@/stores/policeEmployeeStore";
import {usePopulationStore} from "@/stores/populationStore";
import DropDown from "@/components/Misc/DropDown.vue";
import {useDcraStore} from "@/stores/dcraStore";
import { debounce } from "vue-debounce";
import {saveFile} from "@/composable/filesHelper";

// Props
const props = defineProps({
    url: String,
    title: String,
    dataset: String,
    onlyHasOneOri: Boolean,
    enableLinkToEditor: Boolean,
    hasClearError: Boolean,
    hasClearWarning: Boolean,
    editorUrl: String,
    noTotal: Boolean,
});

// State
const dataItems = ref([]);
const numberOfReturnedItems = ref(0);
const isShowFilterEditor = ref(false);
const total = ref(0);
const showLoading = ref(false);
const showUpdateModal = ref(false);
const selectedItem = ref({});
const showErrorDescriptionModal = ref(false);
const selectedErrorDescription = ref("");
const selectedErrorCode = ref("");
const selectedErrorType = ref("");
const selectedErrorBody = ref("");
const showAdditionalColumns = ref(false);
const autoRefreshEnabled = ref(true);
const autoRefreshInterval = "10000ms";
var updateTableDataSource = debounce(() => getData(), autoRefreshInterval);

// Store setup
const uploadType = UploadType[props.dataset ?? ""];
const store = (() => {
    switch (uploadType) {
        case UploadType.contacts:
            return useContactsStore();
        case UploadType.communityPolicing:
            return useCommunityPolicingStore();
        case UploadType.nibrs:
            return useNibrsStore();
        case UploadType.summary:
            return useSummaryStore();
        case UploadType.useOfForce:
            return useUofStore();
        case UploadType.policeEmployee:
            return usePoliceEmployeeStore();
        case UploadType.population:
            return usePopulationStore();
        case UploadType.dcra:
            return useDcraStore();
    }
})();

const apiUrl = store!.apiUrl;

const getValueFromSource = (source, target): string => {
    if (source && target) {
        let value = source[target];
        if (value) {
            return encodeURIComponent(value);
        }
    }
    return "";
};
const getEditorPageName = (item:any) => {
    return getEditorPageNameByIncidentAndOri(getValueFromSource(item, 'incidentNumber'), getValueFromSource(item, 'ori'));
    

}

const getEditorPageNameByIncidentAndOri = (incidentNumber: string, ori: string): string => {
    if (props.editorUrl != undefined) {
        var editorUrl = new URL(props.editorUrl)!;

        editorUrl.searchParams.append("incident", incidentNumber);
        editorUrl.searchParams.append("ori", ori);
        editorUrl.searchParams.append("returnurl", window.location.href);

        return editorUrl.href;
    }

    return "";
};

// Filter options
const filtersJson = sessionStorage.getItem(`ew_${uploadType}_filterOptions`);
const filterOptions = ref(
    filtersJson
        ? JSON.parse(filtersJson)
        : {
              filters: [{property: "status", operation: "eq", value: "Unresolved"}],
          }
);

// Current paging mode
const currentPagingMode = computed(() => {
    if (!props.noTotal || filterOptions.value.filters.some((a) => a.property == "status" && a.value == "Unresolved")) {
        return "withTotal";
    }
    return "noTotal";
});

// Table options
const optionsJson = sessionStorage.getItem(`ew_${uploadType}_tableOptions`);
const options = ref(
    optionsJson
        ? JSON.parse(optionsJson)
        : {
              totalItemCount: 0,
              itemsPerPage: 25,
              page: 1,
              sortBy: [{
            key: "uploadDate",
            isDescending: true,
        },],
              pagingMode: currentPagingMode.value,
              isLastPage: false,
          }
);

// Computed properties
const filterableFields = computed(() =>
    store!.getFilterableFieldsForErrorsAndWarnings(props.onlyHasOneOri, showAdditionalColumns.value, false)
);

const columns = computed(() =>
    store!.getErrorsAndWarningsColumns(props.onlyHasOneOri, showAdditionalColumns.value, false)
);

const hasAppliedFilters = computed(() => filterOptions.value.filters.length > 0);

// Methods
const getData = async () => {
    if (isShowFilterEditor.value) return;
        
    const loadingTimeout = setTimeout(() => {
        showLoading.value = true;
    }, 2000);
    
    options.value.pagingMode = currentPagingMode.value;
    const previousScroll = window.scrollY;

    try {
        const response = await store!.getErrorsAndWarnings(
            filterOptions.value.filters,
            options.value,
            filterableFields.value,
            options.value.sortBy
        );
        
        numberOfReturnedItems.value = response!.data.data.length;
        dataItems.value = response!.data.data.slice(0, options.value.itemsPerPage);

        total.value = response!.data.total;
        options.value.totalItemCount = total.value;

        window.scrollTo({
            top: previousScroll,
            behavior: 'smooth'
        });
    } catch (error) {
        console.error('Error loading errors and warnings:', error?.response?.statusText);
        dataItems.value = [];
    } finally {
        clearTimeout(loadingTimeout);
        showLoading.value = false;
        if (autoRefreshEnabled.value) {
                updateTableDataSource();
            }
    }
};

const handleUpdateModalDismiss = async () => {
    showUpdateModal.value = false;
    await getData();
};

const isStatusLinkEnabled = (item: any) => {
    return (
        (item["errorType"] == "Warning" && props.hasClearWarning) ||
        (item["errorType"] == "Error" && props.hasClearError)
    );
};


const exportData = async (type: string) => {
   const { blob, filename } = await store.exportErrorsAndWarnings(type, filterOptions.value.filters,
            options.value,
            filterableFields.value,
            options.value.sortBy);
    saveFile(filename, blob);
};


const handleStatusClick = (item: any) => {
    if (item != null) {
        var idNumber = props.dataset == "communityPolicing" ? item["stopNumber"] : item["incidentNumber"];

        selectedItem.value = item;
        showUpdateModal.value = true;
    }
};

const handleErrorCodeClick = async (item: any) => {
    let errorCode = item["errorCode"];
    selectedErrorCode.value = "";
    selectedErrorDescription.value = "";
    selectedErrorBody.value = "No information available";

    if (errorCode != undefined && store!.errorDescriptions && store!.errorDescriptions.value) {
        selectedErrorCode.value = errorCode;
        let found = false;
        
        for (const errorDesc of store!.errorDescriptions.value) {
            if (errorDesc.code == errorCode) {
                found = true;
                selectedErrorType.value = item["errorType"];
                selectedErrorDescription.value = errorDesc.help;

                try {
                    const descriptionText = await getHtmlContent(`help.aspx?e=${selectedErrorCode.value}`);
                    if (descriptionText) {
                        selectedErrorBody.value = descriptionText;
                    } else if (selectedErrorDescription.value) {
                        selectedErrorBody.value = selectedErrorDescription.value;
                    }
                } catch (err) {
                    selectedErrorBody.value = selectedErrorDescription.value || "No information available";
                }
                break;
            }
        }
        showErrorDescriptionModal.value = true;
    }
};

// Watchers
watch(
    () => options.value,
    () => {
        sessionStorage.setItem(`ew_${uploadType}_tableOptions`, JSON.stringify(options.value));
        getData();
    },
    {deep: true}
);

watch(
    () => filterOptions.value,
    () => {
        sessionStorage.setItem(`ew_${uploadType}_filterOptions`, JSON.stringify(filterOptions.value));
        options.value.pagingMode = currentPagingMode.value;
        getData();
    },
    {deep: true}
);

// Lifecycle hooks
onMounted(() => {
    getData();
});
</script>
